import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const LocationManagement = () => {
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  
  // Filters and pagination state
  const [filters, setFilters] = useState({
    search: '',
    company: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 5,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [locationData, setLocationData] = useState({
    name: '',
    latitude: '',
    longitude: '',
    company: ''
  });

  // Fetch locations with pagination and filters
  const fetchLocations = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        search: filters.search,
        company: filters.company,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      });

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/locations/admin/paginated?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setLocations(response.data.locations);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Fetch companies for dropdown
  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to fetch companies');
    }
  };

  useEffect(() => {
    fetchLocations(1);
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchLocations(1);
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handlePageChange = (newPage) => {
    fetchLocations(newPage);
  };

  const handleOpenModal = (location = null) => {
    if (location) {
      // If editing an existing location
      console.log('Opening modal for editing location:', location);
      
      // Ensure we have the location ID
      if (!location._id) {
        console.error('Missing location ID');
        toast.error('Could not edit location: missing ID');
        return;
      }
      
      // Determine company ID format - could be object or string
      let companyId;
      if (typeof location.company === 'object' && location.company !== null) {
        companyId = location.company._id;
      } else {
        companyId = location.company;
      }
      
      console.log('Location ID for editing:', location._id);
      console.log('Company ID for editing:', companyId);
      
      // Set editing state
      setEditingLocation({
        _id: location._id,
        id: location.id || ''
      });
      
      // Set form data
      setLocationData({
        name: location.name || '',
        latitude: location.latitude || '',
        longitude: location.longitude || '',
        company: companyId || ''
      });
    } else {
      // If adding a new location
      setEditingLocation(null);
      setLocationData({
        name: '',
        latitude: '',
        longitude: '',
        company: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLocation(null);
    setLocationData({
      name: '',
      latitude: '',
      longitude: '',
      company: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLocationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form submitted!');
    console.log('editingLocation:', editingLocation);
    console.log('editingLocation._id:', editingLocation ? editingLocation._id : 'No editing location');
    console.log('locationData:', locationData);
    
    // Basic validation
    if (!locationData.name.trim()) {
      toast.error('Location name is required');
      return;
    }
    
    // Validate latitude and longitude
    const lat = parseFloat(locationData.latitude);
    const lng = parseFloat(locationData.longitude);
    
    if (!locationData.latitude || isNaN(lat)) {
      toast.error('Valid latitude is required');
      return;
    }
    if (!locationData.longitude || isNaN(lng)) {
      toast.error('Valid longitude is required');
      return;
    }
    if (lat < -90 || lat > 90) {
      toast.error('Latitude must be between -90 and 90');
      return;
    }
    if (lng < -180 || lng > 180) {
      toast.error('Longitude must be between -180 and 180');
      return;
    }
    
    // Company validation
    if (!locationData.company) {
      toast.error('Company is required');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Prepare data with proper number formatting
      const submitData = {
        name: locationData.name.trim(),
        latitude: lat,
        longitude: lng,
        company: locationData.company
      };

      console.log('Prepared submitData:', submitData);

      let response;
      
      if (editingLocation && editingLocation._id) {
        const locationId = editingLocation._id;
        const updateUrl = `${process.env.REACT_APP_API_BASE_URL}/locations/${locationId}`;
        
        console.log('Sending PUT request to:', updateUrl);
        console.log('With data:', JSON.stringify(submitData));
        console.log('Using config:', JSON.stringify(config));
        
        try {
          // Direct PUT request with detailed logging
          const putResponse = await fetch(updateUrl, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(submitData)
          });
          
          if (!putResponse.ok) {
            const errorData = await putResponse.json();
            throw new Error(errorData.error || `Server error: ${putResponse.status}`);
          }
          
          response = await putResponse.json();
          console.log('PUT response received:', response);
          toast.success('Location updated successfully');
          
          // Show Swal with a delay to ensure it's visible
          window.setTimeout(() => {
            Swal.fire('Success', 'Location updated successfully!', 'success');
          }, 300);
        } catch (updateError) {
          console.error('Error in PUT request:', updateError);
          console.error('Error details:', updateError);
          toast.error(`Update failed: ${updateError.message}`);
          Swal.fire('Error', `Error updating location: ${updateError.message}`, 'error');
          throw updateError;
        }
      } else {
        console.log('Sending POST request to:', `${process.env.REACT_APP_API_BASE_URL}/locations`);
        response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/locations`,
          submitData,
          config
        );
        console.log('POST response received:', response.data);
        toast.success('Location created successfully');
        setTimeout(() => {
          Swal.fire('Success', 'Location created successfully!', 'success');
        }, 100);
      }

      handleCloseModal();
      fetchLocations(pagination.currentPage);
    } catch (error) {
      console.error('Error saving location:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        const errorMsg = error.response.data.error || 'Failed to save location';
        toast.error(errorMsg);
        setTimeout(() => {
          Swal.fire('Error', `Error: ${errorMsg}`, 'error');
        }, 100);
      } else if (error.request) {
        console.error('No response received:', error.request);
        toast.error('No response from server. Please check if the backend is running.');
        setTimeout(() => {
          Swal.fire('Error', 'No response from server. Please check if the backend is running.', 'error');
        }, 100);
      } else {
        console.error('Error setting up request:', error.message);
        toast.error(error.message);
        setTimeout(() => {
          Swal.fire('Error', `Error: ${error.message}`, 'error');
        }, 100);
      }
    } finally {
      setLoading(false);
    }
  };



  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-2 mx-1 text-sm rounded-md ${
            i === pagination.currentPage
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="flex items-center justify-between mt-6">
        <div className="text-sm text-gray-700">
          Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{' '}
          {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} of{' '}
          {pagination.totalCount} results
        </div>
        <div className="flex items-center">
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={!pagination.hasPrevPage}
            className="px-3 py-2 text-sm border border-gray-300 rounded-l-md bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {pages}
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={!pagination.hasNextPage}
            className="px-3 py-2 text-sm border border-gray-300 rounded-r-md bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center">
                <svg className="w-8 h-8 mr-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 616 0z" />
                </svg>
                Location Management
              </h1>
              <p className="text-gray-600 mt-2 text-sm">Manage all company locations with precision and style</p>
            </div>
            <button
              onClick={() => handleOpenModal()}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl hover:from-blue-700 hover:to-purple-700 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-sm"
              disabled={loading}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="font-semibold">Add Location</span>
            </button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
              <label className="block text-xs font-bold text-gray-700 mb-2 flex items-center space-x-2">
                <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Search</span>
              </label>
              <input
                type="text"
                placeholder="Search by name or ID..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:shadow-md transition-all duration-200 text-sm"
              />
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
              <label className="block text-xs font-bold text-gray-700 mb-2 flex items-center space-x-2">
                <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span>Company</span>
              </label>
              <select
                value={filters.company}
                onChange={(e) => handleFilterChange('company', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:shadow-md transition-all duration-200 text-sm"
              >
                <option value="">All Companies</option>
                {companies.map(company => (
                  <option key={company._id} value={company._id}>{company.name}</option>
                ))}
              </select>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200">
              <label className="block text-xs font-bold text-gray-700 mb-2 flex items-center space-x-2">
                <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
                <span>Sort By</span>
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:shadow-md transition-all duration-200 text-sm"
              >
                <option value="createdAt">Created Date</option>
                <option value="name">Name</option>
                <option value="id">Location ID</option>
              </select>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200">
              <label className="block text-xs font-bold text-gray-700 mb-2 flex items-center space-x-2">
                <svg className="w-3 h-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                </svg>
                <span>Order</span>
              </label>
              <select
                value={filters.sortOrder}
                onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:shadow-md transition-all duration-200 text-sm"
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>
        </div>

        {/* Locations Table */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
          {loading ? (
            <div className="flex flex-col justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600 mb-4"></div>
              <span className="text-gray-600 font-medium text-lg">Loading locations...</span>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        <div className="flex items-center space-x-2">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                          <span>Location Details</span>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        <div className="flex items-center space-x-2">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                          </svg>
                          <span>Coordinates</span>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        <div className="flex items-center space-x-2">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <span>Company</span>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {locations.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-8 py-16 text-center">
                          <div className="flex flex-col items-center">
                            <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            </svg>
                            <p className="text-lg font-medium text-gray-500">No locations found</p>
                            <p className="text-sm text-gray-400 mt-2">Try adjusting your search or filters</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      locations.map((location, index) => (
                        <tr key={location._id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200 group">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm">
                                {location.name?.charAt(0)?.toUpperCase()}
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
                                  {location.name}
                                </div>
                                <div className="text-xs text-gray-500">ID: {location.id || `LOC-${index + 1}`}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="space-y-1">
                              <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold inline-block">
                                Lat: {location.latitude}
                              </div>
                              <div className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-semibold inline-block ml-2">
                                Lng: {location.longitude}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <div className="bg-green-100 p-1 rounded-lg">
                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-gray-900">
                                  {location.company?.name || 'Unknown Company'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs font-medium">
                            <div className="flex space-x-2">

                              <button
                                onClick={() => handleOpenModal(location)}
                                className="bg-green-100 text-green-600 hover:bg-green-200 p-1 rounded-lg transition-all duration-200 hover:scale-110"
                                title="Edit Location"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {pagination.totalPages > 1 && renderPagination()}
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-2xl mx-4 rounded-3xl shadow-2xl p-8 relative border border-gray-200 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center">
                  <svg className="w-8 h-8 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 616 0z" />
                  </svg>
                  {editingLocation ? 'Edit Location' : 'Add New Location'}
                </h3>
                <p className="text-gray-600 mt-2 text-lg">
                  {editingLocation ? 'Update the location information below' : 'Create a new location with precise coordinates'}
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-3 rounded-full transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Location Name */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200">
                <label className="block text-lg font-bold text-gray-700 mb-4">
                  <div className="flex items-center space-x-3">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <span>Location Name *</span>
                  </div>
                </label>
                <input
                  type="text"
                  name="name"
                  value={locationData.name}
                  onChange={handleInputChange}
                  className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500 focus:border-blue-500 text-lg bg-white shadow-lg hover:shadow-xl transition-all duration-200"
                  placeholder="Enter location name (e.g., Main Warehouse, Branch Office)"
                  required
                />
              </div>

              {/* Coordinates */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border border-purple-200">
                <label className="block text-lg font-bold text-gray-700 mb-4">
                  <div className="flex items-center space-x-3">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    <span>Geographic Coordinates *</span>
                  </div>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-md font-semibold text-gray-600 mb-2">Latitude</label>
                    <input
                      type="number"
                      step="any"
                      name="latitude"
                      value={locationData.latitude}
                      onChange={handleInputChange}
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-500 focus:border-purple-500 text-lg bg-white shadow-lg hover:shadow-xl transition-all duration-200"
                      placeholder="e.g., 40.7128"
                      required
                    />
                    <p className="text-sm text-gray-500 mt-2">Range: -90 to 90</p>
                  </div>
                  <div>
                    <label className="block text-md font-semibold text-gray-600 mb-2">Longitude</label>
                    <input
                      type="number"
                      step="any"
                      name="longitude"
                      value={locationData.longitude}
                      onChange={handleInputChange}
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-purple-500 focus:border-purple-500 text-lg bg-white shadow-lg hover:shadow-xl transition-all duration-200"
                      placeholder="e.g., -74.0060"
                      required
                    />
                    <p className="text-sm text-gray-500 mt-2">Range: -180 to 180</p>
                  </div>
                </div>
              </div>

              {/* Company */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200">
                <label className="block text-lg font-bold text-gray-700 mb-4">
                  <div className="flex items-center space-x-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span>Associated Company *</span>
                  </div>
                </label>
                {editingLocation ? (
                  <div className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl bg-gray-100 text-gray-700 text-lg shadow-lg flex items-center space-x-3">
                    <div className="bg-green-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
                      {(companies.find(company => company._id === locationData.company)?.name || 'U').charAt(0)}
                    </div>
                    <span className="font-semibold">
                      {companies.find(company => company._id === locationData.company)?.name || 'Unknown Company'}
                    </span>
                  </div>
                ) : (
                  <select
                    name="company"
                    value={locationData.company}
                    onChange={handleInputChange}
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-500 focus:border-green-500 text-lg bg-white shadow-lg hover:shadow-xl transition-all duration-200"
                    required
                  >
                    <option value="">Select a company</option>
                    {companies.map(company => (
                      <option key={company._id} value={company._id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-6 pt-8 border-t-2 border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 font-bold text-lg transition-all duration-200 flex items-center space-x-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Cancel</span>
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 flex items-center space-x-3 font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{editingLocation ? 'Update Location' : 'Create Location'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


    </div>
  );
};

export default LocationManagement;